import { CommonModule, NgClass } from '@angular/common';
import { Component, OnDestroy, OnInit, } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { SyllabusResponseAI } from '@edtech-ui/core/models/ai-course/ai-generate-syllabus';
import { CourseManagementService } from '@edtech-ui/core/services/course-management/course-management.service';
import { FileValidationService } from '@edtech-ui/core/services/file-validation/file-validation.service';
import { AddLinkDialogBoxComponent } from '@edtech-ui/dashboard/manual-course-creation/add-link-dialog-box/add-link-dialog-box.component';
import { ToastrService } from 'ngx-toastr';
import { catchError, finalize, Subscription, tap, throwError, timer } from 'rxjs';
import { MatChipsModule } from '@angular/material/chips';
import { AiGenerateDialogBoxComponent } from '../ai-generate-dialog-box/ai-generate-dialog-box.component';

@Component({
	selector: 'app-ai-creation',
	standalone: true,
	imports: [RouterModule, ReactiveFormsModule, MatProgressSpinnerModule, CommonModule, FormsModule, MatIconModule, NgClass, MatTooltipModule,
		MatChipsModule
	],
	templateUrl: './ai-creation.component.html',
	styleUrl: './ai-creation.component.scss'

})
export class AiCreationComponent implements OnInit, OnDestroy {

	generateSyllabusForm!: FormGroup;
	classroomId: string = '';
	subscription = new Subscription();
	_generatedSyllabusData: SyllabusResponseAI | null = null;
	isLoading: boolean = false;
	uploadedImages: any[] = [];
	links: string[] = [];
	isTenantMember:boolean = false;

	constructor(
		private route: ActivatedRoute,
		private _courseService: CourseManagementService,
		private _toastr: ToastrService,
		private router: Router,
		private _fileService: FileValidationService,
		private _dialog: MatDialog,
	) { }

	ngOnInit(): void {
		this.route.parent?.paramMap.subscribe((params) => {
			this.classroomId = params.get('id') || '';
		});

		this.generateSyllabusForm = new FormGroup({
			description: new FormControl(''),
			use_research_tools: new FormControl(false),
			files: new FormControl(null),
			url: new FormControl(null)
		});
         this.isTenantMember = this.getTenantMember();
	}

	generateCourse(): void {
		if (!this.generateSyllabusForm.value.description?.trim() &&
			!(this.generateSyllabusForm.value.files?.length > 0) &&
			!this.generateSyllabusForm.value.url?.trim()) {
			this._toastr.warning('Prompt , File and  Url is required to generate a course!');
			return;
		}

		if (this.generateSyllabusForm.invalid || !this.classroomId) return;

		this.isLoading = true;

		const formData = new FormData();
		formData.append('description', this.generateSyllabusForm.value.description.trim());
		formData.append('use_research_tools', String(this.generateSyllabusForm.value.use_research_tools));

		const files: File[] = this.generateSyllabusForm.value.files || [];
		files.forEach(file => formData.append('files', file));

		if (this.generateSyllabusForm.value.url) {
			formData.append('url', this.generateSyllabusForm.value.url);
		}

		this.subscription.add(
			this._courseService.postGenerateSyllabus(formData, this.classroomId).pipe(
				tap(response => {
					if (response.Success) {
						this._generatedSyllabusData = response;
						const dialogRef = this._dialog.open(AiGenerateDialogBoxComponent);
						timer(7000).subscribe(() => {
							dialogRef.close();
						});
						this._courseService.saveSyllabusData.next(response.Data);
						this.router.navigate(['/teacher-dashboard/classroom-detail-page', this.classroomId, 'courses']);
					} else {
						this._toastr.error(response.Message);
					}
				}),
				finalize(()=>{
					this.isLoading = false;
				}),
				catchError(err => {
                this._toastr.error('Something went wrong while generating the course.');
                return throwError(() => err);
               })
			).subscribe()
		);
	}

	goBackToClassroomDetailPage() {
		this.router.navigate(['/teacher-dashboard/classroom-detail-page', this.classroomId, 'overview']);
	}

	toggleResearchTool() {
		const control = this.generateSyllabusForm.get('use_research_tools');
		if (control) {
			control.setValue(!control.value);
		}
	}

	onFileSelected(event: Event) {
		const input = event.target as HTMLInputElement;
		if (!input.files) return;

		const newFiles: File[] = Array.from(input.files);
		const currentFiles: File[] = this.generateSyllabusForm.value.files || [];

		newFiles.forEach(file => {
			const { fileData, error } = this._fileService.processFile(file);

			if (error) {
				this._toastr.error(error);
				return;
			}

			if (fileData) {
				this.uploadedImages.push(fileData);
			}
		});

		const updatedFiles = [...currentFiles, ...this.uploadedImages.map(f => f.file)];
		this.generateSyllabusForm.patchValue({ files: updatedFiles });

		input.value = '';
	}

	removeImage(img: { file: File, url: string }) {
		this.uploadedImages = this.uploadedImages.filter(i => i !== img);
		const remainingFiles = this.generateSyllabusForm.value.files.filter(
			(f: File) => f !== img.file
		);
		this.generateSyllabusForm.patchValue({ files: remainingFiles });
	}

	removeUrl(index: number) {
		this.links.splice(index, 1);
		this.generateSyllabusForm.patchValue({ url: '' });
	}

	openAddLinkDialogBox() {
		const dialogRef = this._dialog.open(AddLinkDialogBoxComponent, {
			width: '400px'
		});

		dialogRef.afterClosed().subscribe(result => {
			if (result) {
				this.links.push(result);
				this.generateSyllabusForm.patchValue({ url: result });
			}
		})
	}
	getTenantMember(): boolean {
		const isTenantMember = localStorage.getItem('is_tenant_member');
		return isTenantMember ? JSON.parse(isTenantMember) : false;
	}

	openMessageDialogBox() {
		this._dialog.open(AiGenerateDialogBoxComponent);
	}

	ngOnDestroy(): void {
		if (this.subscription) {
			this.subscription.unsubscribe();
		}
	}

}